import { useRef, useEffect } from 'react';

import { env } from '@/env';
import { getRestApiUrl } from '@/utils/getRestApiUrl';

interface Resolution {
  resolve: (url: string | PromiseLike<string>) => void;
  reject: (err: Error) => void;
}

const unsetUploadFn = () => Promise.reject(new Error('No uploader available!'));

export function useImageUploader() {
  const ref = useRef<(file: File) => Promise<string>>(unsetUploadFn);

  useEffect(() => {
    async function run() {
      const { ImageUploader } = await import('@ortoo/upload');
      const uploader = new ImageUploader({
        apiEndpoint: getRestApiUrl(),
        imageUploadUrl: env.NEXT_PUBLIC_IMAGE_UPLOAD_URL,
        imageUploadBucket: env.NEXT_PUBLIC_IMAGE_UPLOAD_BUCKET,
        awsImageUploadAccessKey: env.NEXT_PUBLIC_AWS_IMAGE_UPLOAD_ACCESS_KEY,
        accessToken: '',
        cookieAuth: true,
      });

      const resolutionMap = new Map<number, Resolution>();

      let latestResolution: Resolution | undefined;

      const upload = async (file: File) => {
        uploader.uploadFiles([file]);

        return new Promise<string>((resolve, reject) => {
          latestResolution = { resolve, reject };
        });
      };

      uploader.on('submitted', (id: number) => {
        if (latestResolution) {
          resolutionMap.set(id, latestResolution);
        }

        latestResolution = undefined;
      });

      uploader.on('error', (id: number, name: string, reason: string) => {
        const resolution = resolutionMap.get(id);
        if (resolution) {
          resolution.reject(new Error(reason));
        }
      });

      uploader.on('complete', (id: number) => {
        const resolution = resolutionMap.get(id);
        if (resolution) {
          resolutionMap.delete(id);
          resolution.resolve(uploader.filePath(id));
        } else if (latestResolution) {
          latestResolution.resolve(uploader.filePath(id));
          latestResolution = undefined;
        }
      });

      ref.current = upload;
    }

    run();
  }, []);

  return (file: File) => ref.current(file);
}
